import { AxiosPromise } from 'axios';

import {
  BaseRepository,
  IEnumerableResponse
} from '@app/src/common/repository/BaseRepository';
import {
  IPlayList,
  IPlayListParams,
  IPlayListItemsResponse,
  IPlayListItemParams,
  IPlayListItemPostParams,
  IPlayListSequence
} from '@app/src/components/content-management/model/PlayLists';
import {
  IShareContentDto,
  ISharedItemData,
  ISelectedContentItemFoldersResponse
} from '@app/src/common/model/SharedContent';

export interface IPlayListRequestParams {
  startIndex?: number;
  endIndex?: number;
  sort?: string;
  search?: string;
  statuses?: string;
  scope?: number;
}

class PlayListRepository extends BaseRepository {
  playListUrl = `${this.projectManagementUrl}/playlists`;

  public getPlayLists(
    params?: IPlayListRequestParams
  ): Promise<IEnumerableResponse<IPlayList>> {
    return this.callGet<IEnumerableResponse<IPlayList>, IPlayListRequestParams>(
      this.playListUrl,
      params
    ).then(response => response.data);
  }

  public publishPlayList(playListId: string): AxiosPromise {
    return this.callPut(`${this.playListUrl}/publish`, { ids: [playListId] });
  }

  public rollbackPlayList(playListId: string): AxiosPromise {
    return this.callPut(`${this.playListUrl}/${playListId}/rollback`, {});
  }

  public deletePlayList(playListId: string): AxiosPromise {
    return this.callDelete(`${this.playListUrl}/${playListId}`);
  }

  public deletePlayLists(ids: string[]): AxiosPromise {
    return this.callDelete(`${this.playListUrl}/ids`, {
      ids
    });
  }

  public getPlayList(playListId: string): Promise<IPlayListParams> {
    return this.callGet<IPlayListParams, null>(
      `${this.playListUrl}/${playListId}`
    ).then(response => response.data);
  }

  public getPlayListThumbnail(playListId: string): Promise<{ url: string }> {
    return this.callGet<{ url: string }>(
      `${this.playListUrl}/${playListId}/thumbnail`
    ).then(response => response.data);
  }

  public updatePlayList(
    id: string,
    params: IPlayListParams
  ): AxiosPromise<void> {
    return this.callPut(`${this.playListUrl}/${id}`, params);
  }

  public createPlayList(playList: IPlayListParams): AxiosPromise {
    return this.callPost(this.playListUrl, playList);
  }

  public copyPlayList(playListId: string): AxiosPromise {
    return this.callPost(`${this.playListUrl}/${playListId}/copy`, {});
  }

  public getPlayListItems(
    playListId: string
  ): AxiosPromise<IPlayListItemsResponse> {
    return this.callGet<IPlayListItemsResponse, IPlayListRequestParams>(
      `${this.playListUrl}/${playListId}/items`
    );
  }

  public createPlayListItems(
    playListId: string,
    items: IPlayListItemPostParams[]
  ): AxiosPromise<void> {
    return this.callPost(`${this.playListUrl}/${playListId}/items`, items);
  }

  public updatePlayListItem(
    playListId: string,
    playListItemId: string,
    params: IPlayListItemParams
  ): AxiosPromise<void> {
    return this.callPut(
      `${this.playListUrl}/${playListId}/items/${playListItemId}`,
      params
    );
  }

  public reorderPlayListItem(
    playListId: string,
    items: string[]
  ): AxiosPromise<void> {
    return this.callPut(
      `${this.playListUrl}/${playListId}/items/reorder`,
      items
    );
  }

  public deletePlayListItem(playListId: string, itemId: string): AxiosPromise {
    return this.callDelete(`${this.playListUrl}/${playListId}/items/${itemId}`);
  }

  public deletePlayListItems(
    playListId: string,
    ids: string[]
  ): AxiosPromise<void> {
    return this.callDelete(`${this.playListUrl}/${playListId}/items/ids`, {
      ids
    });
  }

  public getPlayListSequence(
    playListId: string,
    params: any
  ): Promise<IPlayListSequence> {
    return this.callGet<IPlayListSequence, IPlayListRequestParams>(
      `${this.playListUrl}/${playListId}/sequence-preview`,
      params
    ).then(response => response.data);
  }

  public updateVideoDuration(fileId: string): AxiosPromise<void> {
    return this.callPut(
      `${this.playListUrl}/update-video-duration?fileId=${fileId}`,
      {}
    );
  }

  sharePlayList = (id: string, body: IShareContentDto) => {
    return this.callPut(`${this.playListUrl}/${id}/share`, body);
  };

  getSharedItemData = (id: string) => {
    return this.callGet<ISharedItemData>(`${this.playListUrl}/${id}/share`);
  };

  getSharedFoldersIds = (
    id: string
  ): AxiosPromise<ISelectedContentItemFoldersResponse> => {
    return this.callGet<ISelectedContentItemFoldersResponse>(
      `${this.playListUrl}/${id}/folders`
    );
  };
}

export default new PlayListRepository();
